import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { ObjectData } from '@os/doc';
import { embedSrc } from '@os/shared';
import { ExportModeContext, useEmbedActive } from '../editor/embeds';
import type { MediaResolver } from '../editor/lod';
import { safeHref } from '../text/sanitize';
import { useAssetUrl } from './useAssetUrl';

const PROVIDER = { youtube: 'YouTube', vimeo: 'Vimeo', drive: 'Google Drive' } as const;

/**
 * A video or Drive file: a poster until activated, then the iframe the app builds itself (never a user-supplied src).
 * Only one embed plays at a time; any pan, zoom or selection change stops it (the controller deactivates it).
 */
export function EmbedObject({ o, media }: { o: Extract<ObjectData, { type: 'embed' }>; media: MediaResolver }) {
  const { t } = useTranslation();
  const [active, setActive] = useEmbedActive(o.id);
  const exporting = useContext(ExportModeContext); // poster and provider badge only (spec §12)
  const thumb = useAssetUrl(media, o.thumbAssetId, 1024);
  const href = safeHref(o.url);
  const drive = o.provider === 'drive';
  const title = o.title || PROVIDER[o.provider];
  if (active && !exporting && o.embeddable !== false)
    return (
      <iframe
        src={embedSrc(o.provider, o.embedId)}
        title={title}
        sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
        referrerPolicy="strict-origin-when-cross-origin"
        allow="fullscreen; picture-in-picture"
        className="h-full w-full rounded-xl border-0 bg-black"
      />
    );
  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center gap-2 overflow-hidden rounded-xl bg-[#18181b] p-4 text-center font-sans text-white">
      {thumb && <img src={thumb} alt="" draggable={false} className="pointer-events-none absolute inset-0 h-full w-full select-none object-cover opacity-70" />}
      <div className="relative flex flex-col items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-white/70">{PROVIDER[o.provider]}</span>
        <span className="max-w-full truncate text-sm font-semibold">{title}</span>
        {exporting ? null : o.embeddable !== false ? (
          <button
            type="button"
            data-canvas-ignore // its own click: under pointer capture the canvas would retarget it to the viewport
            aria-label={t('links.play', { title })}
            onClick={() => setActive(true)}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-[#141414] hover:bg-white"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M8 5v14l11-7z" fill="currentColor" />
            </svg>
          </button>
        ) : (
          <span className="text-xs text-white/70">{t('links.notEmbeddable')}</span>
        )}
        {!exporting && href && (o.embeddable === false || drive) && (
          <a href={href} target="_blank" rel="noopener noreferrer" data-canvas-ignore className="rounded-md bg-white/90 px-2 py-1 text-xs text-[#141414] hover:bg-white">
            {drive ? t('links.openDrive') : t('links.open')}
          </a>
        )}
        {!exporting && drive && <span className="max-w-xs text-[11px] text-white/70">{t('links.driveNote')}</span>}
      </div>
    </div>
  );
}
